import { useMemo } from 'react';
import type { ProjectTaskStats } from '@hygge/shared';
import { useBoards } from '@/hooks/use-boards';
import { useBoardsTaskStats } from '@/hooks/use-tasks';
import { httpStatus } from '@/lib/http-status';
import { combineTaskStats } from '../lib/project-boards';

/**
 * The task figures of a project as its Dashboard and Progress show them
 * (HW-983): the tasks of every board linked to the project that the reader can
 * open, added up. The boards list is already narrowed to those, so a board the
 * reader may not open counts for nothing here. One request per board, as the
 * boards dashboard asks (`useBoardsTaskStats`).
 *
 * `boardIds` empty with nothing failed means the project has no board this
 * reader can open. Refused the boards list altogether (no Open Boards) is that
 * answer too, not a failure (HW-902).
 */
export function useLinkedBoardsTaskStats(projectId: string, enabled: boolean) {
  const {
    data: boards,
    isLoading: isLoadingBoards,
    isError: isBoardsListError,
    error,
  } = useBoards({ projectId }, { enabled });
  const isBoardsError = isBoardsListError && httpStatus(error) !== 403;
  const boardIds = useMemo(
    () => (enabled ? (boards ?? []).map((b) => b.id) : []),
    [boards, enabled]
  );
  const results = useBoardsTaskStats(boardIds);

  const loaded = results.map((r) => r.data).filter((s): s is ProjectTaskStats => !!s);
  const isLoadingStats = results.some((r) => r.isLoading);
  // A board whose figures failed would make the sum a smaller number that
  // looks right; the sum is shown only when every board answered.
  const isStatsError = results.some((r) => r.isError && !r.data);
  const stats =
    !isStatsError && loaded.length === boardIds.length ? combineTaskStats(loaded) : undefined;

  return {
    boards: boards ?? [],
    boardIds,
    stats,
    isLoading: isLoadingBoards || isLoadingStats,
    isLoadingBoards,
    isBoardsError: isBoardsError && !boards,
    isStatsError,
  };
}
